/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * NotificationAnalyticsPage
 * -------------------------
 * Dashboard view for notification delivery analytics within an application.
 * Shows key metrics (delivery rate, read rate), channel distribution,
 * daily volume, and top event types — all driven by the analytics API.
 *
 * Follows the OrganizationGroupTypesPage pattern: back button header,
 * theme-aware cards, responsive grid, ProgressBarDialog for loading.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ProgressBarDialog from '../components/common/ProgressBar';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

function formatDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function defaultStartDate() {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDate(d);
}

function defaultEndDate() {
    return formatDate(new Date());
}

export default function NotificationAnalyticsPage() {
    const { theme } = useTheme();
    const { id: applicationId } = useParams();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();

    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        try {
            // Convert local selected dates to UTC range for the backend
            // For start date, we want 00:00:00 in local time
            const startLocal = new Date(`${startDate}T00:00:00`);
            // For end date, we want 23:59:59 in local time
            const endLocal = new Date(`${endDate}T23:59:59`);

            const resp = await apiHelper.apiCall('/arcee/notifications/analytics/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    application_id: parseInt(applicationId, 10),
                    start_date: startLocal.toISOString(),
                    end_date: endLocal.toISOString()
                })
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error || `Failed to load analytics (${resp.status})`);
            }
            const data = await resp.json();
            setAnalytics(data);
        } catch (error) {
            showAlertDialog({
                title: 'Error',
                message: error.message || 'Failed to load analytics',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    }, [applicationId, startDate, endDate, showAlertDialog]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const metrics = analytics?.metrics || {};
    const channelDist = analytics?.channel_distribution || [];
    const dailyVolume = analytics?.daily_volume || {};
    const topTemplates = analytics?.top_templates || [];

    const metricCards = [
        { label: 'Total Sent', value: metrics.total_sent ?? 0, icon: 'bi-send', color: theme.primary },
        { label: 'Delivered', value: metrics.total_delivered ?? 0, icon: 'bi-check-circle', color: '#16a34a' },
        { label: 'Failed', value: metrics.total_failed ?? 0, icon: 'bi-x-circle', color: '#dc2626' },
        { label: 'Delivery Rate', value: `${metrics.delivery_rate ?? 0}%`, icon: 'bi-percent', color: '#0891b2' },
        { label: 'Read Rate', value: `${metrics.read_rate ?? 0}%`, icon: 'bi-eye', color: '#7c3aed' }
    ];

    const dailyVolumeEntries = (Array.isArray(dailyVolume)
        ? dailyVolume.map(item => [item.day, item.count])
        : Object.entries(dailyVolume)
    ).sort((a, b) => String(a[0]).localeCompare(String(b[0])));

    return (
        <div className="d-flex flex-column w-100" style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
            {/* Header */}
            <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-light rounded-circle shadow-sm"
                        onClick={() => navigate(`/dashboard/applications/${applicationId}`)}
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark">Notification Analytics</h4>
                        <small className="text-muted d-none d-md-block">
                            Monitor delivery performance and engagement metrics.
                        </small>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <button
                        className="btn d-flex align-items-center gap-2"
                        onClick={() => {
                            const isNestedUnderControls = window.location.pathname.includes('/notification-controls/');
                            if (isNestedUnderControls) {
                                navigate(`/dashboard/applications/${applicationId}/notification-controls`);
                            } else {
                                navigate(`/dashboard/applications/${applicationId}/notification-analytics/notification-controls`);
                            }
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.primary;
                            e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = theme.primary;
                        }}
                        style={{
                            borderRadius: theme.buttonRadius || '8px',
                            border: `1px solid ${theme.primary}`,
                            color: theme.primary,
                            background: 'transparent',
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <i className="bi bi-sliders"></i>
                        Controls
                    </button>
                </div>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1400 }}>

                {/* Date Range Filter */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: theme.cardRadius }}>
                    <div className="card-body py-3 px-4">
                        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-calendar3 text-muted"></i>
                                <label className="small fw-bold text-muted mb-0">From</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={startDate}
                                    max={defaultEndDate()}
                                    onChange={e => {
                                        const v = e.target.value;
                                        if (v > defaultEndDate()) return;
                                        setStartDate(v);
                                    }}
                                    style={{ maxWidth: 160, borderRadius: theme.inputRadius || '6px' }}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <label className="small fw-bold text-muted mb-0">To</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={endDate}
                                    max={defaultEndDate()}
                                    onChange={e => {
                                        const v = e.target.value;
                                        if (v > defaultEndDate()) return;
                                        setEndDate(v);
                                    }}
                                    style={{ maxWidth: 160, borderRadius: theme.inputRadius || '6px' }}
                                />
                            </div>
                            <button
                                className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                                onClick={fetchAnalytics}
                                disabled={loading || startDate > endDate}
                                style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius }}
                            >
                                <i className="bi bi-arrow-clockwise"></i>
                                Refresh
                            </button>
                            {startDate > endDate && (
                                <span className="text-danger small"><i className="bi bi-exclamation-circle me-1"></i>Start date must be before end date</span>
                            )}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <ProgressBarDialog open={true} text_message="Loading analytics..." />
                ) : (
                    <>
                        {/* Metric Cards */}
                        <div className="row g-3 mb-4">
                            {metricCards.map((card, idx) => (
                                <div className="col-6 col-md-4 col-lg" key={idx}>
                                    <div
                                        className="card border-0 shadow-sm h-100"
                                        style={{ borderRadius: theme.cardRadius, borderTop: `3px solid ${card.color}` }}
                                    >
                                        <div className="card-body p-3 text-center">
                                            <div className="mb-2">
                                                <i
                                                    className={`bi ${card.icon}`}
                                                    style={{ fontSize: '1.5rem', color: card.color }}
                                                ></i>
                                            </div>
                                            <div className="fw-bold fs-4" style={{ color: card.color }}>
                                                {card.value}
                                            </div>
                                            <div className="text-muted small mt-1">{card.label}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Data Sections */}
                        <div className="row g-4">
                            {/* Channel Distribution */}
                            <div className="col-12 col-lg-4">
                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: theme.cardRadius }}>
                                    <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-broadcast text-primary" style={{ fontSize: '1.2rem' }}></i>
                                            <h6 className="fw-bold mb-0 text-dark">Channel Distribution</h6>
                                        </div>
                                    </div>
                                    <div className="card-body px-4 pt-3">
                                        {channelDist.length === 0 ? (
                                            <div className="text-muted text-center py-4 small">No data available</div>
                                        ) : (
                                            <div className="d-flex flex-column gap-3">
                                                {channelDist.map((ch, idx) => {
                                                    const maxCount = Math.max(...channelDist.map(c => c.count), 1);
                                                    const pct = Math.round((ch.count / maxCount) * 100);
                                                    return (
                                                        <div key={idx}>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span className="small fw-semibold text-capitalize">
                                                                    {(ch.channel_type || '').replace(/_/g, ' ')}
                                                                </span>
                                                                <span className="small text-muted">{ch.count}</span>
                                                            </div>
                                                            <div className="progress" style={{ height: 8, borderRadius: 4, background: '#f1f5f9' }}>
                                                                <div
                                                                    className="progress-bar"
                                                                    style={{ width: `${pct}%`, background: theme.primary, borderRadius: 4, transition: 'width 0.5s ease' }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Daily Volume */}
                            <div className="col-12 col-lg-4">
                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: theme.cardRadius }}>
                                    <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-bar-chart-line text-info" style={{ fontSize: '1.2rem' }}></i>
                                            <h6 className="fw-bold mb-0 text-dark">Daily Volume</h6>
                                        </div>
                                    </div>
                                    <div className="card-body px-4 pt-3">
                                        {dailyVolumeEntries.length === 0 ? (
                                            <div className="text-muted text-center py-4 small">No data available</div>
                                        ) : (
                                            <div className="table-responsive" style={{ maxHeight: 300, overflowY: 'auto' }}>
                                                <table className="table table-sm table-borderless mb-0">
                                                    <thead>
                                                        <tr style={{ fontSize: '12px', color: theme.primary }}>
                                                            <th className="fw-semibold">Date</th>
                                                            <th className="fw-semibold text-end">Count</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {dailyVolumeEntries.map(([day, count]) => (
                                                            <tr key={day} style={{ fontSize: '13px' }}>
                                                                <td className="text-muted">{day}</td>
                                                                <td className="text-end fw-semibold">{count}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Top Templates */}
                            <div className="col-12 col-lg-4">
                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: theme.cardRadius }}>
                                    <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-trophy text-warning" style={{ fontSize: '1.2rem' }}></i>
                                            <h6 className="fw-bold mb-0 text-dark">Top Event Types</h6>
                                        </div>
                                    </div>
                                    <div className="card-body px-4 pt-3">
                                        {topTemplates.length === 0 ? (
                                            <div className="text-muted text-center py-4 small">No data available</div>
                                        ) : (
                                            <div className="d-flex flex-column gap-2">
                                                {topTemplates.map((t, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="d-flex align-items-center justify-content-between p-2 rounded"
                                                        style={{ background: idx === 0 ? `${theme.primary}08` : 'transparent' }}
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span
                                                                className="badge rounded-pill"
                                                                style={{
                                                                    background: idx === 0 ? theme.primary : '#e2e8f0',
                                                                    color: idx === 0 ? '#fff' : '#475569',
                                                                    width: 24,
                                                                    height: 24,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '11px'
                                                                }}
                                                            >{idx + 1}</span>
                                                            <span className="small fw-semibold">{t.name || t.event_type}</span>
                                                        </div>
                                                        <span className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: '12px' }}>
                                                            {t.count}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div >
    );
}
